import { toSentenceCase } from '@noths/polaris-client-utils';
import { DocumentProps } from 'next/document';

import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { PageEventCategoryType, PageEventObject, PageType } from 'src/types';
import { logger } from 'src/utils/serverLogger';

export type PageProps = DocumentProps & { updatedReduxState: ReduxApplicationState };

export const UNDEFINED_PAGE_TYPE_ERROR = 'Page tracking event - Undefined page type';
export const UNKNOWN_PAGE_TYPE_ERROR = 'Page tracking event - Unknown page type';

const getListId = (pageType: PageType, pageProps: PageProps) => {
  switch (pageType) {
    case PageType.Category:
      return String(pageProps.updatedReduxState.products.category!.id);
    case PageType.Search:
      return '15000';
    case PageType.Partner:
      return String(pageProps.updatedReduxState.products.partner?.id);
    default:
      return '';
  }
};

const getListName = (pageType: PageType, pageProps: PageProps) => {
  switch (pageType) {
    case PageType.Category:
      return pageProps.updatedReduxState.products.category!.name;
    case PageType.Search:
      return pageProps.updatedReduxState.products.searchTerm;
    case PageType.Partner:
      return pageProps.updatedReduxState.products.partner?.name;
    default:
      return '';
  }
};

/**
 * This is to add the page object immediately into datalayer before
 * the gtm.start tag fires
 */
export const getServerSidePageEventObject = (
  pageProps: PageProps,
  version: string,
  appVersion: string,
): PageEventObject => {
  if (!pageProps || !pageProps.updatedReduxState) {
    return {} as PageEventObject;
  }

  try {
    const { filter, products, navigation } = pageProps.updatedReduxState;

    if (!navigation || !navigation.pageType) {
      throw new Error(UNDEFINED_PAGE_TYPE_ERROR);
    }

    const pageType = navigation.pageType;

    const listId = getListId(pageType, pageProps);
    const listName = getListName(pageType, pageProps);

    if (!listId || !listName) {
      throw new Error(UNKNOWN_PAGE_TYPE_ERROR);
    }

    const { totalProducts, items, queryType, category } = products;

    const pageEventObject: PageEventObject = {
      page: {
        category: toSentenceCase(String(pageType)) as PageEventCategoryType,
        environment: process.env.NODE_ENV,
        release_version: version,
        app_version: appVersion,
        filter_list: filter.collections.filters.map((f) => f.title).join(','),
        // Gets set on page load when nav type is calculated
        filter_nav_type: '',
        list_id: listId,
        list_name: listName,
        loop54_query_type: queryType,
        react: 'true',
        total_listing_items: totalProducts,
        total_sponsored_items: Object.values(items)[0]?.reduce((count, item) => {
          return item.isSponsored ? count + 1 : count;
        }, 0),
        ...(category?.typeOfCategory && { type_of_category: category.typeOfCategory }),
      },
    };

    return pageEventObject;
  } catch (error) {
    logger.warn({
      location: 'generatePageEventObject',
      extra: { error },
    });

    return {} as PageEventObject;
  }
};
